import { FileText, FileImage, File, CheckCircle2 } from 'lucide-react'
import {
  chipIconKind,
  formatFileSize,
  type ChipPhase,
} from './uploadChipModel'

interface Props {
  filename: string
  size: number
  mime?: string | null
  phase: ChipPhase
  progress?: number
  error?: string | null
}

export default function UploadFileChip({
  filename,
  size,
  mime,
  phase,
  progress = 0,
  error,
}: Props) {
  const kind = chipIconKind(filename, mime)
  const Icon = kind === 'pdf' ? FileText : kind === 'image' ? FileImage : kind === 'doc' ? FileText : File

  return (
    <div
      data-testid="upload-file-chip"
      data-phase={phase}
      style={{
        border: '1px solid var(--line)',
        borderRadius: 'var(--rs)',
        padding: '10px 12px',
        background: 'var(--panel)',
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Icon size={18} color="var(--ink3)" aria-hidden />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--ink)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={filename}
          >
            {filename}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink5)' }}>{formatFileSize(size)}</div>
        </div>
        {phase === 'success' && (
          <CheckCircle2 size={18} color="var(--green, #16a34a)" data-testid="upload-chip-success" />
        )}
      </div>
      {phase === 'uploading' && (
        <div
          data-testid="upload-chip-progress"
          style={{
            marginTop: 8,
            height: 4,
            borderRadius: 2,
            background: 'var(--line2)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${Math.min(100, Math.max(4, progress))}%`,
              background: 'var(--accent)',
              transition: 'width 0.15s ease',
            }}
          />
        </div>
      )}
      {phase === 'error' && error && (
        <p
          data-testid="upload-chip-error"
          style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--red)' }}
        >
          {error}
        </p>
      )}
    </div>
  )
}
